import React, { useMemo } from 'react';

import type { ToggleableKeys, VerticalAlign } from '@aline/common';
import { AlineTooltip, SelectButton } from 'src/components';
import { BoldIcon } from 'src/components/icons/BoldIcon';
import { ItalicIcon } from 'src/components/icons/ItalicIcon';
import { StrikethroughIcon } from 'src/components/icons/StrikethroughIcon';
import { SubscriptIcon } from 'src/components/icons/SubscriptIcon';
import { SuperscriptIcon } from 'src/components/icons/SuperscriptIcon';
import { UnderlineIcon } from 'src/components/icons/UnderlineIcon';
import type { FontProperties } from 'src/editor/editor-derived-state';
import { useEditorTabStateSelector } from 'src/editor/editor-tab-state-registry';
import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserId } from 'src/gql';
import { useAuthorizedTransforms } from 'src/hooks';
import { useEditor, useEditorLoading } from 'src/layout-engine';
import { makeStyles } from 'tss-react/mui';
export const boldButtonAttribute = 'toolbar-button-fontstyle-bold';
export const italicButtonAttribute = 'toolbar-button-fontstyle-italic';
export const underlineButtonAttribute = 'toolbar-button-fontstyle-underline';
export const strikethroughButtonAttribute = 'toolbar-button-fontstyle-strikethrough';

const useStyles = makeStyles()(theme => ({
  button: {
    height: 32,
    margin: theme.spacing(0, 0.25),
    width: 32,
  },
  buttonContainer: {
    display: 'flex',
  },
}));

type ButtonFormats = Pick<
  FontProperties,
  'bold' | 'italic' | 'underline' | 'strikethrough' | 'superscript' | 'subscript'
>;
type ButtonValue = 'bold' | 'italic' | 'underline' | 'strikethrough' | 'superscript' | 'subscript';

export const TextFormatButtons = React.memo(function TextFormatButtons(): JSX.Element {
  const { classes } = useStyles();
  const editor = useEditor();
  const currentUserId = useCurrentUserId();
  const editorLoading = useEditorLoading();
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const bold = useEditorTabStateSelector(state => state.derived.fontProperties.bold);
  const editingMode = useEditorTabStateSelector(state => state.documentUi.editingMode);
  const italic = useEditorTabStateSelector(state => state.derived.fontProperties.italic);
  const strikethrough = useEditorTabStateSelector(
    state => state.derived.fontProperties.strikethrough
  );
  const subscript = useEditorTabStateSelector(state => state.derived.fontProperties.subscript);
  const superscript = useEditorTabStateSelector(state => state.derived.fontProperties.superscript);
  const underline = useEditorTabStateSelector(state => state.derived.fontProperties.underline);

  const activeButtons = useMemo(() => {
    const buttonFormats: ButtonFormats = {
      bold,
      italic,
      strikethrough,
      subscript,
      superscript,
      underline,
    };

    const buttons: ButtonValue[] = [
      'bold',
      'italic',
      'underline',
      'strikethrough',
      'superscript',
      'subscript',
    ];

    return buttons.filter(formatType => buttonFormats[formatType]);
  }, [bold, italic, strikethrough, subscript, superscript, underline]);

  function handleTextFormatting(formatKey: ToggleableKeys) {
    return function (): void {
      if (!currentUserId || !editingMode) return;

      AuthorizedTransforms.toggleTextFormatting(editor, {
        authorId: currentUserId,
        editingMode,
        formatKey,
      });

      ClientTransforms.focus(editor);
    };
  }

  function handleVerticalAlign(verticalAlign: VerticalAlign) {
    return function (): void {
      if (!currentUserId || !editingMode) return;

      AuthorizedTransforms.toggleVerticalAlign(editor, {
        authorId: currentUserId,
        editingMode,
        verticalAlign,
      });

      ClientTransforms.focus(editor);
    };
  }

  return (
    <div className={classes.buttonContainer}>
      <AlineTooltip arrow placement="bottom" title="Bold">
        <div>
          <SelectButton
            className={classes.button}
            data-cy={boldButtonAttribute}
            disabled={editorLoading}
            selected={activeButtons.includes('bold')}
            onClick={handleTextFormatting('bold')}
          >
            <BoldIcon />
          </SelectButton>
        </div>
      </AlineTooltip>
      <AlineTooltip arrow placement="bottom" title="Italic">
        <div>
          <SelectButton
            className={classes.button}
            data-cy={italicButtonAttribute}
            disabled={editorLoading}
            selected={activeButtons.includes('italic')}
            onClick={handleTextFormatting('italic')}
          >
            <ItalicIcon />
          </SelectButton>
        </div>
      </AlineTooltip>
      <AlineTooltip arrow placement="bottom" title="Underline">
        <div>
          <SelectButton
            className={classes.button}
            data-cy={underlineButtonAttribute}
            disabled={editorLoading}
            selected={activeButtons.includes('underline')}
            onClick={handleTextFormatting('underline')}
          >
            <UnderlineIcon />
          </SelectButton>
        </div>
      </AlineTooltip>
      <AlineTooltip arrow placement="bottom" title="Strikethrough">
        <div>
          <SelectButton
            className={classes.button}
            data-cy={strikethroughButtonAttribute}
            disabled={editorLoading}
            selected={activeButtons.includes('strikethrough')}
            onClick={handleTextFormatting('strikethrough')}
          >
            <StrikethroughIcon />
          </SelectButton>
        </div>
      </AlineTooltip>
      <AlineTooltip arrow placement="bottom" title="Superscript">
        <div>
          <SelectButton
            className={classes.button}
            disabled={editorLoading}
            selected={activeButtons.includes('superscript')}
            onClick={handleVerticalAlign('superscript')}
          >
            <SuperscriptIcon />
          </SelectButton>
        </div>
      </AlineTooltip>
      <AlineTooltip arrow placement="bottom" title="Subscript">
        <div>
          <SelectButton
            className={classes.button}
            disabled={editorLoading}
            selected={activeButtons.includes('subscript')}
            onClick={handleVerticalAlign('subscript')}
          >
            <SubscriptIcon />
          </SelectButton>
        </div>
      </AlineTooltip>
    </div>
  );
});
